// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// `/pricing` — what it costs, before anybody signs in (G3-09; 08 §2, 08 §3, 01 §6).
//
// **08 §2 marks this route `Anonymous`, and that word is the whole reason it exists separately from
// `/account`.** The subscription panel shows the same two prices and is behind the account bundle:
// a visitor who wants to know what this costs before making an account has to make one first, which
// is the shape of upsell this product's own arcade page argues against at length.
//
// So the decision G3-09 asks for is taken this way round — the route exists — and the reason is
// that the alternative is a price nobody can read without an identity.
//
// **It reaches nothing.** `pricingCatalog.ts` is a table and says at the top of itself that it is
// deliberately outside the account bundle so the front door may read it (FR-030); this page is a
// page of it. That is also why it is not lazy: a static table split into its own chunk is a network
// round trip to display four numbers.
//
// **The annual plan is the anchor** (08 §3). It is shown first, at its full price, with the monthly
// equivalent underneath and the saving stated — which is anchoring used the honest way round: the
// larger number is the one that is actually charged, and the smaller one beside it is a division
// anybody can check. `monthlyEquivalent` exists for exactly this and is documented as never being
// used to charge anything.
//
// **No prices are typed here.** `pricingCatalog` is the single display source, which is the rule
// G3-09 restates and which matters because a second copy of a price is a page that says one thing
// and a checkout that charges another.

import { Link } from 'react-router'
import {
  annualSavingPercent,
  formatMoney,
  monthlyEquivalent,
  paidAdds,
  plansIn,
  type Currency,
  type PricingPlan,
} from '../../ui/pricingCatalog'
import { readCurrency } from '../i18n/currency'
import { CopyFaq } from '../components/CopyFaq'
import { CopySections } from '../components/CopySections'
import { PAGE_COPY } from '../../seo/pageCopy'
import { useLocalisation } from '../i18n/useLanguage'

export function Pricing(): React.JSX.Element {
  const { t, locale } = useLocalisation()
  const currency: Currency = readCurrency()
  const plans = plansIn(currency)
  // Annual first, whatever order the table holds them in. The anchor is a decision about this page
  // rather than about the catalogue, so it is made here and the catalogue keeps its own order.
  const ordered = [...plans].sort((a, b) => (a.id === 'annual' ? -1 : b.id === 'annual' ? 1 : 0))
  const saving = annualSavingPercent(currency)

  return (
    <div className="page">
      {/* Promoted from an h2 (S3-04): with the top bar no longer claiming the h1, this page has
          one top-level heading and it is the one that says what the page is. */}
      <h1>{t('pricing.heading')}</h1>
      <p className="page__lede">{t('pricing.lede')}</p>

      <div className="roster">
        {ordered.map((plan) => (
          <PlanCard key={plan.id} plan={plan} locale={locale} saving={saving} />
        ))}
      </div>

      <h3>{t('pricing.included')}</h3>
      {/*
        What Pro adds, from the capability table the gates themselves read.

        The labels are English prose in the catalogue rather than message keys, and that is a
        pre-existing decision this page inherits rather than re-litigates: `SubscriptionPanel` draws
        the same strings the same way. What matters here is that there is one list — a second copy
        of what a subscription buys is a page that keeps a row somebody removed from the gate.
      */}
      <ul className="readout">
        {paidAdds().map((capability) => (
          <li key={capability.id}>{capability.label}</li>
        ))}
      </ul>

      <p className="page__lede">{t('pricing.free-forever')}</p>

      <Link className="button button--primary" to="/account?want=full-roster" title={t('pricing.cta-title')}>
        {t('pricing.cta')}
      </Link>
      {/*
        **The one thing on this page a visitor can try before paying for it** (M10-25).

        Two of the rows in the list above cannot be demonstrated at any price — the roster is gated
        at the definition fetch, the headset needs hardware, the arcade needs other people — and one
        of them opens for anybody: 01 §6's *"the Builder is a sandbox, not a locked door"*. It was
        listed here as a bullet and linked from nowhere, so the page named the strongest argument it
        had and gave the reader no way to act on it.

        Second, after the price and before the way out. A visitor who is not going to subscribe today
        leaves through one of these two doors, and the one that ends with them holding something they
        made is the better place to send them.
      */}
      <Link className="button" to="/builder" title={t('builder.open-title')}>
        {t('builder.open')}
      </Link>
      {/*
        **The second door, for the reader the list above just told about the AI tools** (M12-30).

        The capability list names building with your own AI and the questions below answer it, and
        until this the only way to reach the instructions was the footer. Beside the Builder rather
        than above it: somebody who wants to try something now opens the Builder, and somebody who
        wants their own AI to do it is setting up a tool and will come back.
      */}
      <Link className="button" to="/connect" title={t('package.how')}>
        {t('route.title.connect')}
      </Link>
      <Link className="button" to="/" title={t('play.browse-title')}>
        {t('pricing.play')}
      </Link>
      {/*
        What the plans mean, in words (S3-07). Below the plans and the buttons, so the decision is
        still the first thing on the page — and **no price is written here**: prices come from the
        payment provider per currency, and a number in this file would be a number that is wrong
        somewhere.
      */}
      <CopySections sections={PAGE_COPY['/pricing']?.below} />
      {/*
        The questions, last (S6-04, M12-30). They are also the page's `FAQPage` markup, which the
        prerenderer writes from the same field — so this component is not optional decoration:
        without it the page would carry structured data for text a visitor cannot read.
      */}
      <CopyFaq questions={PAGE_COPY['/pricing']?.faq} />
    </div>
  )
}

function PlanCard({
  plan,
  locale,
  saving,
}: {
  readonly plan: PricingPlan
  readonly locale: string
  readonly saving: number | null
}): React.JSX.Element {
  const { t } = useLocalisation()
  const annual = plan.id === 'annual'
  return (
    <div className={annual ? 'tile tile--build' : 'tile'}>
      <span className="tile__name">{t(annual ? 'pricing.annual' : 'pricing.monthly')}</span>
      <span className="tile__meta">{formatMoney(plan.price, locale)}</span>
      {/*
        The monthly equivalent, on the annual card only. On the monthly card it would be the same
        number twice; here it is the division that makes the anchor checkable rather than rhetorical.
      */}
      {annual ? (
        <span className="tile__meta">
          {t('pricing.per-month', { amount: formatMoney(monthlyEquivalent(plan), locale) })}
        </span>
      ) : null}
      {annual && saving !== null ? (
        <span className="tile__badge tile__badge--free">{t('pricing.saving', { percent: saving })}</span>
      ) : null}
    </div>
  )
}
